<template>
  <validation-observer ref="refFormObserver">
    <b-form slot-scope="{ validate }" @submit.prevent="validate().then(onSubmit)">
      <!-- Parent Group -->
      <validation-provider #default="validationContext" name="Parent Group">
        <b-form-group label="Parent Group*" label-for="item-group-parent" class="mb-3">
          <v-select
            v-model="itemGroupFormData.parentID"
            :options="parentGroupOptions"
            :reduce="(option) => option.id"
            label="name"
            placeholder="Select parent group"
            :clearable="true"
            :state="getValidationState(validationContext)"
            :disabled="loading"
          />

          <b-form-invalid-feedback>
            {{ validationContext.errors[0] }}
          </b-form-invalid-feedback>
        </b-form-group>
      </validation-provider>

      <!-- Group Code -->
      <b-form-group label="Group Code" class="mb-3">
        <form-input
          v-model="itemGroupFormData.code"
          :disabled="loading"
          name="Group Code"
          rules="required"
          placeholder="Enter group code"
        />
      </b-form-group>

      <!-- Group Name -->
      <b-form-group label="Group Name" class="mb-3">
        <form-input
          v-model="itemGroupFormData.name"
          :disabled="loading"
          name="Group Name"
          rules="required"
          placeholder="Enter group name"
        />
      </b-form-group>

      <!-- Group Type -->
      <validation-provider #default="validationContext" name="Group Type" rules="required">
        <b-form-group label="Group Type" label-for="item-group-type" class="mb-3">
          <b-form-select
            id="item-group-type"
            v-model="itemGroupFormData.groupType"
            :options="groupTypeOptions"
            :state="getValidationState(validationContext)"
            :disabled="loading"
            placeholder="Select group type"
          >
            <template #first>
              <option value="" disabled>Select group type</option>
            </template>
          </b-form-select>

          <b-form-invalid-feedback>
            {{ validationContext.errors[0] }}
          </b-form-invalid-feedback>
        </b-form-group>
      </validation-provider>

      <!-- Item Type (only visible when group type is control) -->
      <validation-provider
        v-if="itemGroupFormData.groupType === 'C'"
        #default="validationContext"
        name="Item Type"
        rules="required"
      >
        <b-form-group label="Item Type" label-for="item-group-item-type" class="mb-3">
          <b-form-select
            id="item-group-item-type"
            v-model="itemGroupFormData.itemType"
            :options="itemTypeOptions"
            :state="getValidationState(validationContext)"
            :disabled="loading"
            placeholder="Select item type"
          >
            <template #first>
              <option value="" disabled>Select item type</option>
            </template>
          </b-form-select>

          <b-form-invalid-feedback>
            {{ validationContext.errors[0] }}
          </b-form-invalid-feedback>
        </b-form-group>
      </validation-provider>

      <!-- Is Active -->
      <b-form-group label="Status" class="mb-3">
        <b-form-checkbox
          id="item-group-active"
          v-model="itemGroupFormData.isActive"
          name="item-group-active"
          value="true"
          unchecked-value="false"
          :disabled="loading"
        >
          Active
        </b-form-checkbox>
      </b-form-group>

      <b-button type="submit" variant="primary" :disabled="loading">
        <b-spinner small v-if="loading" class="mr-1"></b-spinner>
        {{ loading ? 'Saving...' : 'Save Item Group' }}
      </b-button>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormSelect,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
} from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from '@vue/composition-api';
import formValidation from '@core/comp-functions/forms/form-validation';
import store from '@/store';
import { api } from '@/utils/api';
import FormInput from '@/views/apps/fbr-invoice/components/form/FormInput.vue';
import vSelect from 'vue-select';

export default {
  name: 'ItemGroupForm',
  components: {
    BForm,
    BFormGroup,
    BFormSelect,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BSpinner,

    // Form Validation
    ValidationObserver,
    ValidationProvider,

    // Custom Components
    FormInput,
    vSelect,
  },
  model: {
    prop: 'formData',
    event: 'update:form-data',
  },
  props: {
    formData: {
      type: Object | null,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const businessID = computed(() => store.state.user.businessID);

    const itemGroupFormData = ref(
      props.formData
        ? JSON.parse(JSON.stringify(props.formData))
        : {
            id: 0,
            parentID: null,
            code: '',
            name: '',
            groupType: 'D',
            itemType: '',
            isActive: true,
            businessID: businessID.value,
          }
    );

    const parentGroupOptions = ref([]);
    const groupTypeOptions = [
      { value: 'C', text: 'Control' },
      { value: 'D', text: 'Detail' },
    ];
    const itemTypeOptions = [
      { value: 'Goods', text: 'Goods' },
      { value: 'Services', text: 'Services' },
    ];

    // Fetch parent groups for dropdown
    const fetchParentGroups = async () => {
      try {
        const response = await api.get(`/ItemGroups/GetByPagination/${businessID.value}`, {
          params: {
            businessID: businessID.value,
            pageNumber: 1,
            pageSize: 1000,
            searchTerm: '',
          },
        });

        console.log('=== fetchParentGroups response:', response);

        // Handle response structure based on store module
        let items = [];
        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          items = response.data.data.items;
        } else if (response.data && response.data.data && response.data.data.items) {
          items = response.data.data.items;
        } else if (response.data && response.data.items) {
          items = response.data.items;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        console.log('=== items extracted:', items);

        // Map items to dropdown options
        parentGroupOptions.value = items.map((item) => ({
          id: item.id,
          name: `${item.code} - ${item.name}`,
        }));

        console.log('=== parentGroupOptions:', parentGroupOptions.value);
      } catch (error) {
        console.error('Error fetching parent groups:', error);
        // Fallback with test data to ensure dropdown works
        parentGroupOptions.value = [
          { id: 1, name: 'Test Group 1 - Electronics' },
          { id: 2, name: 'Test Group 2 - Clothing' },
        ];
        console.log('=== Using fallback parentGroupOptions:', parentGroupOptions.value);
      }
    };

    const onSubmit = async () => {
      console.log('Emitting form data:', JSON.stringify(itemGroupFormData.value, null, 2));
      emit('submit', itemGroupFormData.value);
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(() => {
      itemGroupFormData.value = {
        id: 0,
        parentID: null,
        code: '',
        name: '',
        groupType: 'D',
        itemType: '',
        isActive: true,
        businessID: businessID.value,
      };
    });

    watch(
      itemGroupFormData,
      (newVal) => {
        emit('update:form-data', newVal);
      },
      { deep: true }
    );

    // Watch for groupType changes to handle itemType visibility
    watch(
      () => itemGroupFormData.value.groupType,
      (newGroupType) => {
        if (newGroupType !== 'C') {
          // If group type is not Control, clear item type
          itemGroupFormData.value.itemType = '';
        }
      }
    );

    // Fetch parent groups when component mounts
    onMounted(() => {
      console.log('=== ItemGroupForm onMounted, businessID:', businessID.value);
      if (businessID.value) {
        fetchParentGroups();
      } else {
        console.error('BusinessID not available');
      }
    });

    return {
      itemGroupFormData,
      parentGroupOptions,
      groupTypeOptions,
      itemTypeOptions,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss" scoped>
.b-card {
  margin-bottom: 1rem;
}
</style>
